import React, { useState, useEffect } from "react";
import { Tabs, Tab, Typography, makeStyles } from "@material-ui/core";
import { useLocation } from "react-router-dom";
import API from "../../../API";
import { PersonalInFormationForm } from "./PersonalInFormationForm";
import { NEW_PATIENT_FORM_VALIDATION_SCHEMA } from "../../../libs";
import { PatientCustomQuestion } from "./PatientCustomQuestion";

const useStyles = makeStyles(theme => ({
    clientForm: {
        padding: "60px"
    },
    main: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    },
    root: {
        maxWidth: "100%",
        marginTop: "40px",
        border: "1px solid #00000050",
        padding: "30px ",
        borderRadius: "17px"
    },
    heading: {
        fontSize: "22px",
        fontWeight: 700,
        textAlign: "center",
        marginBottom: "30px"
    },
    button: {
        position: "absolute",
        height: "fit-content",
        padding: "8px 10px",
        right: "10px",
        margin: "0 !important",
        lineHeight: "normal",
        top: "41px",
        backgroundColor: "#4d5ccc !important",
        color: "#fff",
        ":hover": {
            backgroundColor: "#4d5ccc !important"
        }
    },
    submit: {
        backgroundColor: "#4d5ccc",
        color: "#fff",
        minWidth: "120px",
        maxWidth: "120px",
        margin: "auto",
        marginBottom: "10px",
        "&:hover": {
            backgroundColor: "#4d5ccc !important"
        }
    },
    successMessage: {
        color: "green",
        marginBottom: "20px",
        textAlign: "center",
        maxWidth: "775px",
        margin: "auto"
    },
    errorMessage: {
        marginBottom: "20px",
        color: "#f44336 !important",
        fontSize: "12px !important",
        marginTop: "6px",
        fontFamily: "Roboto"
    },
    codeField: {
        "& div>div": {
            margin: "16px 0 0 0 !important"
        }
    },
    errMsg: {
        color: "#f44336 !important",
        margin: "0",
        fontFamily: "Roboto",
        lineHeight: "normal",
        fontSize: "12px !important"
    },
    tabsDiv: {
        "& div>div": {
            justifyContent: "space-around",
            backgroundColor: " #F0F0F0",
            maxWidth: "600px",
            margin: "auto",
            borderRadius: "12px"
        },
        "& button": {
            width: "100%",
            maxWidth: "300px",
            "&.Mui-selected": {
                color: "#FFF !important",
                position: "relative",
                zIndex: "1"
            }
        },
        "& span": {
            height: "100%",
            borderRadius: "12px 0 0 12px",
            clipPath: "polygon(1% 0, 100% 0%, 90% 100%, 0% 100%)"
        }
    },
    activeTabsContainer: {
        "& span": {
            borderRadius: "0px 12px  12px 0px",
            clipPath: "polygon(0 0, 100% 0, 100% 100%, 10% 100%)"
        }
    },

    "@media (max-width:768px) ": {
        clientForm: {
            padding: "60px 30px"
        }
    },

    "@media (max-width:680px) ": {
        tabsDiv: {
            "& div>div": {
                maxWidth: "440px"
            },
            "& button": {
                maxWidth: "220px"
            }
        }
    },
    "@media (max-width:520px) ": {
        clientForm: {
            padding: "60px 16px"
        },
        tabsDiv: {
            "& div>div": {
                maxWidth: "320px"
            },
            "& button": {
                maxWidth: "160px"
            }
        },
        root: {
            padding: "15px "
        }
    }
}));

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

export const PatientPersonalInformationForm = () => {
    const classes = useStyles();
    const query = useQuery();
    const therapistID = query.get("therapistID");
    const clientId = query.get("clientID");
    const ids = query.get("ids")?.split(",") || [];
    const [selectedIds, setSelectedIds] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [err, setErr] = useState({});
    const [isSecondTabDisabled, setIsSecondTabDisabled] = useState(true);
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);

    const [clientData, setClientData] = useState({
        nameFirst: "",
        nameLast: "",
        address: "",
        town: "",
        post: "",
        email: "",
        CPRLast4Digit: "",
        CPR6Digit: "",
        phonePrivate: "",
        phoneWork: "",
        phoneRelative: "",
        civilStatus: "",
        sex: "",
        education: "",
        children: "",
        doctor: "",
        otherTreater: ""
    });

    const [sucMes, setSucMes] = useState("");
    const [errMes, setErrMes] = useState("");
    const [errors, setErrors] = useState({});
    const [formFields, setFormFields] = useState([]);

    useEffect(() => {
        /**
         * Fetches all client form fields from the server and updates the list of questions.
         * Includes both saved and unsaved questions.
         *
         * @throws Will log an error if the questions cannot be fetched.
         */
        const fetchQuestions = async () => {
            try {
                const savedQuestions = await API.clientGetAllClientFormFieldsByTherapistId(therapistID);
                if (clientId) {
                    const data = await API.clientGetData(clientId);
                    setClientData(data);
                }
                setQuestions(savedQuestions); // Keep unsaved fields
            } catch (error) {
                console.error("Error fetching questions:", error);
            }
        };

        fetchQuestions();
    }, []);

    useEffect(() => {
        /**
         * Fetches client data for the given clientId from the server.
         * Sets the received data to the clientData state.
         * If the request fails, logs the error message to the console.
         * @async
         * @returns {Promise<void>} - A promise that resolves when the operation is complete.
         */
        const fetchClientData = async () => {
            try {
                const storedData = localStorage.getItem("clientData");

                // Parse stored data to check if it exists and matches the current clientId
                if (storedData) {
                    const parsedData = JSON.parse(storedData);
                    if (parsedData.id === clientId) {
                        setClientData(parsedData);
                        return;
                    }
                    return;
                }

                const data = await API.clientGetData(clientId, therapistID);

                const storage = localStorage.setItem("clientData", JSON.stringify(data));

                setClientData(data);
                setFormFields(data.formFields);
            } catch (error) {
                console.error("Error fetching client data:", error);
            }
        };

        if (clientId) {
            fetchClientData();
        }
    }, [clientId]);

    /**
     * Asynchronously fetches the last 4 digits of the CPR number from the API
     * and updates the client data in both localStorage and the state.
     *
     * If successful, the function retrieves existing client data from localStorage,
     * updates the CPRLast4Digit with the fetched result, and stores the updated data
     * back in localStorage along with a timestamp. The client data state is also updated.
     *
     * @async
     * @returns {Promise<void>} - A promise that resolves when the operation is complete.
     */

    const get4lastDigitCPR = async () => {
        var result = await API.get4LastDigitCPR();
        if (result) {
            // Retrieve existing data from localStorage
            const storedData = JSON.parse(localStorage.getItem("clientData"));

            // Extract previous clientData or use an empty object
            const prevData = storedData?.value || {};

            // Update only CPRLast4Digit
            const updatedData = {
                ...prevData,
                CPRLast4Digit: result.lastDigit
            };

            // Store updated data in localStorage
            const dataToStore = {
                value: updatedData,
                timestamp: new Date().getTime() // Store current time in milliseconds
            };
            localStorage.setItem("clientData", JSON.stringify(dataToStore));

            // Update state
            setClientData(updatedData);
        }
    };

    /**
     * Handles changes to a form field by updating the state and localStorage.
     * @param {number} id - The id of the field to update.
     * @param {string} key - The name of the field to update.
     * @param {string} value - The new value of the field.
     * @returns {void}
     */
    const handleChange = (id, key, value) => {
        setFormFields(prevFields => {
            const fieldIndex = prevFields.findIndex(field => field.clientFormFieldsId === id);
            let updatedFields;
            if (fieldIndex !== -1) {
                // Update existing field
                updatedFields = [...prevFields];
                updatedFields[fieldIndex] = { ...updatedFields[fieldIndex], [key]: value };
            } else {
                // Add new field
                updatedFields = [...prevFields, { clientFormFieldsId: id, [key]: value }];
            }

            saveToLocalStorage(updatedFields);
            return updatedFields;
        });
        // Clear error for this field when user starts typing/selecting
        setErrors(prevErrors => {
            if (prevErrors[id]) {
                const newErrors = { ...prevErrors };
                delete newErrors[id];
                return newErrors;
            }
            return prevErrors;
        });
    };

    /**
     * Saves the given fields to localStorage with an expiration time.
     * @param {Array<object>} fields - The fields to save.
     * The fields should be an array of objects with the properties `clientFormFieldsId`, `fieldName`, and `fieldType`.
     * @returns {void}
     */
    const saveToLocalStorage = fields => {
        const storedData = JSON.parse(localStorage.getItem("clientData"));
        const data = {
            ...storedData,
            fields
        };
        localStorage.setItem("clientData", JSON.stringify(data));
    };

    /**
     * Loads the form fields from localStorage and checks if the data has expired.
     * If it has, the data is removed and an empty array is returned.
     * @returns {Array<object>} The loaded form fields, or an empty array if the data is expired.
     */
    const loadFromLocalStorage = () => {
        const storedData = localStorage.getItem("clientData");
        if (!storedData) return []; // If no data is found, return an empty array
        try {
            const parsedData = JSON.parse(storedData);
            if (Array.isArray(parsedData.fields)) {
                return parsedData.fields; // Return only the fields array
            }
        } catch (error) {
            console.error("Error parsing localStorage data:", error);
        }
        return [];
    };

    useEffect(() => {
        setFormFields(loadFromLocalStorage());
    }, []);

    /**
     * Handle tab change event.
     * @param {object} _event The event that triggered the change.
     * @param {number} newValue The new value of the selected tab.
     */
    const handleTabChange = (_event, newValue) => {
        setTabIndex(newValue);
        if (newValue.length > 0) {
            setIsSecondTabDisabled(false);
        }
    };

    const handleCheckboxChange = id => {
        setSelectedIds(prev => (prev.includes(id) ? prev.filter(qId => qId !== id) : [...prev, id]));
    };

    const changeValue = type => async value => {
        try {
            // if (type === "CPR6Digit" || type === "CPRLast4Digit" || type === "email") {
            //     await NEW_PATIENT_FORM_VALIDATION_SCHEMA.validateAt(type, { [type]: value });
            // }
            setErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[type]; // Remove error if input is valid
                return newErrors;
            });
        } catch (err) {
            setErrors(prevErrors => ({
                ...prevErrors,
                [type]: err.message // Set specific error message
            }));
        }

        const storedData = JSON.parse(localStorage.getItem("clientData")) || {};

        setClientData(prevData => {
            const updatedData = { ...prevData, [type]: value };

            // Store updated data in localStorage with a timestamp
            const dataToStore = {
                value: updatedData,
                ...storedData.fields,
                timestamp: new Date().getTime() // Store current time in milliseconds
            };
            localStorage.setItem("clientData", JSON.stringify(dataToStore));

            return updatedData;
        });
    };

    useEffect(() => {
        const storedData = localStorage.getItem("clientData");
        if (storedData) {
            const { value, timestamp } = JSON.parse(storedData);
            const now = new Date().getTime();
            const expiryTime = 20 * 60 * 1000; // 20 minutes in milliseconds

            if (now - timestamp < expiryTime) {
                setClientData(value); // Load valid data
            } else {
                localStorage.removeItem("clientData"); // Remove expired data
            }
        }
    }, []);

    /**
     * Handles sending the personal information form data to the API.
     * Validates the data using Yup and then sends a request to the API to update the client.
     * If the request is successful, sets a success message.
     * If the request fails, sets an error message.
     * If the validation fails, sets a map of error messages.
     */

    const filteredQuestions = questions.filter(question => ids.includes(question.id));

    const sendMail = async () => {
        try {
            setLoading(true);
            // Validate required fields
            let validationErrors = {};
            filteredQuestions.forEach(question => {
                const fieldValue = formFields.find(field => field.clientFormFieldsId === question.id)?.fieldValue || "";

                if (question.isRequired) {
                    if ((question.fieldType === "text" || question.fieldType === "number") && !fieldValue.trim()) {
                        validationErrors[question.id] = `${question.fieldName} is required`;
                    } else if (question.fieldType === "checkbox" && !fieldValue) {
                        validationErrors[question.id] = `${question.fieldName} must be checked`;
                    }
                }
            });
            // If there are errors, update state and stop submission
            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                setLoading(false);

                return;
            }
            let success;

            const client = {
                ...clientData,
                ...(clientId && { id: clientId }),
                CPR: clientData.CPR6Digit + clientData.CPRLast4Digit,
                formFields
            };
            if (clientId && therapistID) {
                success = await API.clientUpdate(client, therapistID);
            } else {
                success = await API.clientRegister(client, therapistID);
            }
            // if (success) {
            setLoading(false);

            localStorage.removeItem("clientData");
            setSucMes("Dine oplysninger er blevet gemt!");
            setClientData({
                nameFirst: "",
                nameLast: "",
                address: "",
                town: "",
                post: "",
                email: "",
                CPRLast4Digit: "",
                CPR6Digit: "",
                phonePrivate: "",
                phoneWork: "",
                phoneRelative: "",
                civilStatus: "",
                sex: "",
                education: "",
                children: "",
                doctor: "",
                otherTreater: ""
            });
            setFormFields([]);
            setErrors({});
            // }
        } catch (error) {
            setLoading(false);

            // Handle API or other errors
            setErrMes(error);
        }
    };

    /**
     * Validates the clientData using Yup and then calls handleTabChange
     * to switch to the next step in the patient registration process.
     * If the validation fails, sets a map of error messages.
     * If the request fails, sets an error message.
     */
    const validateAndNextStep = async () => {
        try {
            // Validate clientData using Yup
            await NEW_PATIENT_FORM_VALIDATION_SCHEMA.validate(
                {
                    email: clientData.email,
                    CPRLast4Digit: clientData.CPRLast4Digit,
                    CPR6Digit: clientData.CPR6Digit,
                    nameFirst: clientData.nameFirst,
                    nameLast: clientData.nameLast,
                    address: clientData.address,
                    town: clientData.town,
                    post: clientData.post
                },
                { abortEarly: false }
            );
            if (ids?.length === 0) {
                sendMail();
            }
            handleTabChange("", 1);
        } catch (error) {
            if (error.name === "ValidationError") {
                // Collect validation errors and update state
                const errors = {};
                error.inner.forEach(err => {
                    errors[err.path] = err.message;
                });
                setErrors(errors);
            } else {
                // Handle API or other errors
                setErrMes(error);
            }
        }
    };

    const form = (
        <PersonalInFormationForm
            classes={classes}
            clientData={clientData}
            changeValue={changeValue}
            get4lastDigitCPR={get4lastDigitCPR}
            setClientData={setClientData}
            // onSave={onSave}
            handleTabChange={handleTabChange}
            errors={errors}
            isPatient={true}
            validateAndNextStep={validateAndNextStep}
            ids={ids}
            loading={loading}
        />
    );

    setTimeout(() => {
        setErr("");
        setSucMes("");
    }, 10000);

    const success = (
        <Typography variant="body1" className={classes.successMessage}>
            {sucMes}
        </Typography>
    );

    const error = (
        <Typography variant="h6" className={classes.errorMessage}>
            {errMes}
        </Typography>
    );
    return (
        <div className={classes.clientForm}>
            {/* Tabs Navigation */}

            {ids.length === 0 && sucMes && success}
            {ids.length === 0 && errMes && error}
            {ids.length === 0 && form}
            {ids.length > 0 && (
                <>
                    <Tabs
                        className={`${classes.tabsDiv} ${tabIndex === 1 ? classes.activeTabsContainer : ""}`}
                        value={tabIndex}
                        indicatorColor="primary"
                        textColor="primary"
                        onChange={handleTabChange}
                        aria-label="Custom Questions Tabs">
                        <Tab label="Personlige oplysninger" />
                        <Tab label="Spørgsmål" disabled={isSecondTabDisabled} />
                    </Tabs>

                    {sucMes && success}
                    {errMes && error}

                    {/* Tab Content */}
                    {tabIndex === 0 && form}

                    {tabIndex === 1 && (
                        <PatientCustomQuestion
                            formFields={formFields}
                            handleChange={handleChange}
                            selectedIds={selectedIds}
                            handleCheckboxChange={handleCheckboxChange}
                            sendMail={sendMail}
                            err={err}
                            errors={errors}
                            ids={ids}
                            questions={questions}
                            loading={loading}
                            handleTabChange={handleTabChange}
                        />
                    )}
                </>
            )}
        </div>
    );
};
